import type React from "react"
import { useEffect, useRef, useState } from "react"

import HighlightedTextarea from "@/components/highlighted-textarea"
import PlatformIcon from "@/components/icons/platform-icon"
import { cn } from "@/lib/utils"
import type { MentionHandleGroup } from "@/types"

const MAX_SUGGESTIONS = 8

type HighlightedTextareaProps = React.ComponentProps<typeof HighlightedTextarea>

type Props = Omit<
  HighlightedTextareaProps,
  "value" | "onChange" | "textareaRef"
> & {
  value: string
  onValueChange: (value: string) => void
  mentionHandleGroups: MentionHandleGroup[]
}

const MENTION_PATTERN = /@(\w*)$/

function detectMentionContext(value: string, caret: number) {
  const before = value.slice(0, caret)
  const match = MENTION_PATTERN.exec(before)
  if (!match) return null

  const mentionStart = caret - match[0].length
  const charBefore = mentionStart > 0 ? value[mentionStart - 1] : ""
  if (charBefore && /\w/.test(charBefore)) return null

  const charAfter = value[caret] ?? ""
  if (charAfter && /\w/.test(charAfter)) return null

  return { query: match[1], mentionStart }
}

function filterSuggestions(
  groups: MentionHandleGroup[],
  query: string,
): MentionHandleGroup[] {
  const lower = query.toLowerCase()
  const matches = lower
    ? groups.filter((g) => g.label.toLowerCase().startsWith(lower))
    : groups
  return matches.slice(0, MAX_SUGGESTIONS)
}

export default function MentionSuggestionTextarea({
  value,
  onValueChange,
  mentionHandleGroups,
  onKeyDown,
  onBlur,
  ...rest
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [mentionStart, setMentionStart] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)

  const suggestions = open ? filterSuggestions(mentionHandleGroups, query) : []
  const exactMatch =
    query.length > 0 &&
    mentionHandleGroups.some(
      (g) => g.label.toLowerCase() === query.toLowerCase(),
    )
  const showDropdown = open && !exactMatch && suggestions.length > 0
  const safeActiveIndex =
    suggestions.length > 0 ? Math.min(activeIndex, suggestions.length - 1) : 0

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  function syncFromCaret(nextValue: string, caret: number) {
    const context = detectMentionContext(nextValue, caret)
    if (context) {
      setOpen(true)
      setQuery((prev) => {
        if (prev !== context.query) setActiveIndex(0)
        return context.query
      })
      setMentionStart((prev) => {
        if (prev !== context.mentionStart) setActiveIndex(0)
        return context.mentionStart
      })
    } else {
      setOpen(false)
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const nextValue = e.target.value
    onValueChange(nextValue)
    syncFromCaret(nextValue, e.target.selectionStart ?? nextValue.length)
  }

  function handleCaretEvent() {
    const textarea = textareaRef.current
    if (!textarea) return
    syncFromCaret(textarea.value, textarea.selectionStart ?? 0)
  }

  function insertSuggestion(group: MentionHandleGroup) {
    const textarea = textareaRef.current
    if (!textarea) return
    const caret = textarea.selectionStart ?? value.length
    const before = value.slice(0, mentionStart)
    const after = value.slice(caret)
    const needsSpace = after.startsWith(" ") ? "" : " "
    const insertion = `@${group.label}${needsSpace}`
    const nextValue = before + insertion + after
    const nextCaret = mentionStart + insertion.length

    onValueChange(nextValue)
    setOpen(false)

    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(nextCaret, nextCaret)
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setActiveIndex((safeActiveIndex + 1) % suggestions.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setActiveIndex(
          safeActiveIndex === 0 ? suggestions.length - 1 : safeActiveIndex - 1,
        )
        return
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault()
        const selected = suggestions[safeActiveIndex]
        if (selected) insertSuggestion(selected)
        return
      }
      if (e.key === "Escape") {
        e.preventDefault()
        setOpen(false)
        return
      }
    }
    onKeyDown?.(e)
  }

  function handleBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    blurTimeoutRef.current = setTimeout(() => setOpen(false), 120)
    onBlur?.(e)
  }

  return (
    <div className="relative">
      <HighlightedTextarea
        {...rest}
        value={value}
        textareaRef={textareaRef}
        onChange={handleChange}
        onClick={handleCaretEvent}
        onFocus={handleCaretEvent}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
      {showDropdown && (
        <ul
          role="listbox"
          aria-label="Mention suggestions"
          className="bg-popover text-popover-foreground absolute top-full left-0 z-20 mt-1 max-h-64 w-full max-w-sm overflow-auto rounded-md border shadow-md"
        >
          {suggestions.map((group, index) => {
            const isActive = index === safeActiveIndex
            return (
              <li
                key={group.id}
                role="option"
                aria-selected={isActive}
                className={cn(
                  "flex cursor-pointer flex-col gap-1 px-3 py-2 text-sm",
                  isActive && "bg-accent text-accent-foreground",
                )}
                onMouseDown={(e) => {
                  e.preventDefault()
                  insertSuggestion(group)
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="font-mono">@{group.label}</span>
                {group.mention_handles.length > 0 && (
                  <span className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    {group.mention_handles.map((h) => (
                      <span key={h.id} className="flex items-center gap-1">
                        <PlatformIcon
                          platform={h.platform.name}
                          className="size-3"
                        />
                        <span className="font-mono">{h.handle}</span>
                      </span>
                    ))}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
